import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Meta, Display, Chip, Button, Icons } from '../components/Atoms';
import ImageLightbox from '../components/ImageLightbox';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { PlantIllustration, HerbariumCorner } from '../components/Illustrations';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, categoryTone } from '../lib/api';
import VarietyReviewHint from '../components/VarietyReviewHint';

/**
 * Plant Detail - szczegóły rośliny z prawdziwą bazy.
 *
 * Hierarchia obrazu hero (z 04-component-composition.md):
 *   1. plant.photo_path (jeśli istnieje) → real photo z gradient overlay
 *   2. PlantIllustration SVG mapowane do kategorii
 *
 * Layout:
 *   Mobile: stack pionowy (hero / stats / opis / care / akcje)
 *   Desktop: split 1.1fr (hero) / 1fr (data)
 */

const TONE_COLOR = {
  sage:  'var(--t-sage)',
  rust:  'var(--t-rust)',
  amber: 'var(--t-amber)',
};

function safeParseJson(str, fallback) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

function categoryLabel(category, t) {
  if (!category) return '—';
  const map = {
    vegetable: 'Warzywo',
    herb: 'Zioło',
    flower_perennial: 'Kwiat wieloletni',
    flower_annual: 'Kwiat jednoroczny',
    flower_bulb: 'Kwiat cebulowy',
    fruit_tree: 'Drzewo owocowe',
    fruit_bush: 'Krzew owocowy',
    tree_ornamental: 'Drzewo ozdobne',
    shrub_ornamental: 'Krzew ozdobny',
    climber: 'Pnącze',
    grass: 'Trawa',
    groundcover: 'Roślina okrywowa',
    succulent: 'Sukulent',
    fern: 'Paproć',
  };
  return map[category] || category;
}

// ── Stat tile ────────────────────────────────────────────────────
function Stat({ lab, val, unit }) {
  return (
    <div style={{ background: 'var(--t-bg-elev)', padding: '14px 16px' }}>
      <Meta style={{ marginBottom: 6 }}>{lab}</Meta>
      <Display size={20}>{val}</Display>
      {unit && (
        <div style={{
          fontSize: 10, color: 'var(--t-text-mute)', marginTop: 2,
          fontFamily: 'var(--t-mono)', letterSpacing: '.1em', textTransform: 'uppercase',
        }}>{unit}</div>
      )}
    </div>
  );
}

// ── Care box ─────────────────────────────────────────────────────
// min-width:0 + overflow-wrap żeby długie wartości (np. "pH 6.5-7.0 lekka")
// nie rozciągały kontenera grid poza viewport mobile.
function CareBox({ Icon, iconColor, lab, val, sub }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      minWidth: 0,
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {Icon && <Icon color={iconColor} size={18} />}
        <Meta>{lab}</Meta>
      </div>
      <Display size={18} style={{
        display: 'block',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
      }}>{val || '—'}</Display>
      {sub && <div style={{
        fontSize: 11, color: 'var(--t-text-mute)', marginTop: 3,
        overflowWrap: 'break-word', wordBreak: 'break-word',
      }}>{sub}</div>}
    </div>
  );
}

// ── Hero ────────────────────────────────────────────────────────
function PlantHero({ plant, huge }) {
  const tone = categoryTone(plant.category);
  const color = TONE_COLOR[tone];
  const colorRaw = tone === 'amber' ? '#dcb464' : tone === 'rust' ? '#c06a4a' : '#a8c49e';
  const hasPhoto = !!plant.photo_path;
  const plantName = plant.display_name || plant.name;
  const [lightbox, setLightbox] = useState(null);

  const nameSize = huge ? 'clamp(64px, 9vw, 120px)' : 'clamp(48px, 12vw, 72px)';
  const varietySize = huge ? 'clamp(24px, 3.5vw, 36px)' : 'clamp(20px, 5vw, 26px)';

  return (
    <>
    <div>
      <Meta style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <span>{categoryLabel(plant.category)}{plant.subcategory ? ` · ${plant.subcategory}` : ''}</span>
        <span>#{String(plant.id).padStart(3, '0')}</span>
      </Meta>

      <div className={huge ? 't-dropcap' : undefined} style={{
        fontFamily: 'var(--t-display)', fontWeight: 300,
        fontSize: nameSize, lineHeight: 0.88,
        letterSpacing: '-.04em', color: 'var(--t-text)',
      }}>{plant.display_name || plant.name}</div>

      {plant.latin_name && (
        <div className="t-latin" style={{
          fontSize: varietySize,
          marginTop: huge ? 18 : 12,
          letterSpacing: '-.015em',
        }}>{plant.latin_name}</div>
      )}

      {/* Hero image area — karta herbarza z podwójną ramką + 4 narożnikami.
          Klikalna → pełnoekranowy lightbox (wcześniej background-image był nieotwieralny). */}
      <div className="t-herbarium"
        onClick={hasPhoto ? () => setLightbox({ src: plant.photo_path, alt: plantName }) : undefined}
        role={hasPhoto ? 'button' : undefined}
        aria-label={hasPhoto ? `Powiększ zdjęcie: ${plantName}` : undefined}
        style={{
        marginTop: huge ? 32 : 24,
        height: huge ? 320 : 220,
        background: hasPhoto
          ? `linear-gradient(180deg, transparent 50%, rgba(15,20,16,0.6) 100%), url(${plant.photo_path}) center / cover`
          : `linear-gradient(135deg, ${colorRaw}26, ${colorRaw}08)`,
        display: hasPhoto ? 'flex' : 'grid',
        flexDirection: 'column-reverse',
        placeItems: hasPhoto ? undefined : 'center',
        position: 'relative', overflow: 'hidden',
        cursor: hasPhoto ? 'zoom-in' : 'default',
      }}>
        <span className="t-herbarium-corner tl"><HerbariumCorner /></span>
        <span className="t-herbarium-corner tr"><HerbariumCorner /></span>
        <span className="t-herbarium-corner br"><HerbariumCorner /></span>
        <span className="t-herbarium-corner bl"><HerbariumCorner /></span>
        {hasPhoto && (
          <div aria-hidden="true" style={{
            position: 'absolute', bottom: 12, right: 12,
            width: 34, height: 34, borderRadius: 17,
            background: 'rgba(15, 20, 16, 0.55)', border: '1px solid var(--t-line-2)',
            display: 'grid', placeItems: 'center', pointerEvents: 'none',
          }}>
            <Icons.Search color="var(--t-text)" size={15} />
          </div>
        )}
        {!hasPhoto && (
          <PlantIllustration
            category={plant.category || 'sprout'}
            tone={tone}
            size={huge ? 180 : 120}
          />
        )}

        {plant.is_perennial === 1 && (
          <div style={{ position: 'absolute', top: 14, left: 14 }}>
            <Chip tone="sage">Wieloletnia</Chip>
          </div>
        )}

        {plant.bloom_season && (
          <div style={{
            position: hasPhoto ? 'static' : 'absolute',
            top: hasPhoto ? undefined : 14, right: hasPhoto ? undefined : 14,
            padding: hasPhoto ? '14px 18px' : 0,
            color: hasPhoto ? 'var(--t-text)' : undefined,
          }}>
            <Meta color={hasPhoto ? 'sage' : tone}>
              Kwitnie · {plant.bloom_season}
            </Meta>
          </div>
        )}

        {!hasPhoto && (
          <div style={{
            position: 'absolute', bottom: 14, right: 14,
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.16em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)',
          }}>
            Brak zdjęcia
          </div>
        )}
      </div>

      {/* Ilustracja historyczna (vintage botanical) — pokazuje się tylko jeśli
          plant ma illustration_path. Mała karta pod hero, klikalna do pełnego
          podglądu. Obecnie 778/1082 plant (72%) ma vintage z atlasów takich jak
          USDA Pomological Watercolors, Vilmorin Andrieux 1900-1904, Hedrick. */}
      {plant.illustration_path && (
        <div
          onClick={() => setLightbox({
            src: plant.illustration_path,
            alt: `${plantName} — ilustracja historyczna`,
            caption: `${plant.illustration_author || 'Atlas botaniczny'}${plant.illustration_source ? ` · ${plant.illustration_source}` : ''}`,
          })}
          role="button"
          aria-label={`Powiększ ilustrację historyczną: ${plantName}`}
          style={{
          marginTop: 16, padding: '12px 14px',
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          borderRadius: 12,
          display: 'flex', gap: 14, alignItems: 'center',
          cursor: 'zoom-in',
        }}>
          <img
            src={plant.illustration_thumb || plant.illustration_path}
            alt={`${plant.display_name || plant.name} — ilustracja historyczna`}
            style={{
              width: 72, height: 72, objectFit: 'cover',
              borderRadius: 8, flexShrink: 0,
              background: 'var(--t-bg)',
            }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Meta color="amber">📜 Ilustracja historyczna</Meta>
            <div style={{
              fontSize: 12, color: 'var(--t-text-dim)',
              marginTop: 4, lineHeight: 1.4,
            }}>
              {plant.illustration_author || 'Atlas botaniczny'}
              {plant.illustration_source ? ` · ${plant.illustration_source}` : ''}
            </div>
          </div>
        </div>
      )}

      {/* Tags row (jeśli jakieś flagi) */}
      {(plant.is_fragrant || plant.is_bee_friendly || plant.is_edible) && (
        <div style={{ marginTop: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {plant.is_edible === 1   && <Chip tone="sage">Jadalna</Chip>}
          {plant.is_fragrant === 1 && <Chip tone="amber">Pachnąca</Chip>}
          {plant.is_bee_friendly === 1 && <Chip tone="amber">Miododajna</Chip>}
        </div>
      )}
    </div>
    <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}

// ── Quote / opis (z plant.notes) ─────────────────────────────────
function PlantQuote({ notes }) {
  if (!notes) return null;
  return (
    <div style={{
      padding: '20px 24px', borderRadius: 14,
      background: 'var(--t-bg-elev)',
      borderLeft: '3px solid var(--t-amber)',
      fontSize: 14, lineHeight: 1.55,
      color: 'var(--t-text)',
    }}>
      {notes}
    </div>
  );
}

// ── FamilySection (P4) — z tej samej rodziny ──────────────────
function FamilySection({ plantId, mobile }) {
  const { data, loading, error } = useApi(plantId ? `/api/plants/${plantId}/relatives` : null);

  if (loading || error || !data || !data.relatives || data.relatives.length === 0) return null;

  return (
    <div style={{
      padding: mobile ? '0 24px' : '0',
      marginBottom: 22,
    }}>
      <Meta style={{ marginBottom: 10 }}>
        Z tej samej rodziny · {data.plant.familyLabel}
      </Meta>
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap',
      }}>
        {data.relatives.slice(0, 8).map(r => (
          <Link key={r.id} to={`/plants/${r.id}`} style={{ textDecoration: 'none' }}>
            <Chip tone="sage">{r.name}</Chip>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ plant, onAddToGarden }) {
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      {/* Back nav */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 20px 0',
      }}>
        <Link to="/plants?restore=1" style={{ textDecoration: 'none' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            height: 36, padding: '0 14px 0 10px', borderRadius: 10,
            background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
            color: 'var(--t-text)',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.16em', textTransform: 'uppercase',
          }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M12 4l-6 6 6 6" stroke="var(--t-text)" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Katalog
          </div>
        </Link>
      </div>

      <div style={{ padding: '24px 24px 20px' }}>
        <PlantHero plant={plant} />
      </div>

      {/* Stats grid */}
      {(plant.days_to_harvest > 0 || plant.spacing || plant.hardiness_zone) && (
        <div style={{
          margin: '0 24px 22px',
          display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1, background: 'var(--t-line-2)',
          borderRadius: 14, overflow: 'hidden',
        }}>
          {plant.days_to_harvest > 0 && (
            <Stat lab="Do zbioru" val={plant.days_to_harvest} unit="dni" />
          )}
          {plant.spacing && (
            <Stat lab="Rozstaw" val={plant.spacing} />
          )}
          {plant.hardiness_zone && (
            <Stat lab="Strefa" val={plant.hardiness_zone} />
          )}
        </div>
      )}

      {plant.notes && (
        <div style={{ margin: '0 24px 22px' }}>
          <PlantQuote notes={plant.notes} />
        </div>
      )}

      {/* F3 — Twoje oceny tej rośliny */}
      <div style={{ padding: '0 24px' }}>
        <VarietyReviewHint plantName={plant.display_name || plant.name} />
      </div>

      {/* Care */}
      <div style={{ padding: '0 24px', marginBottom: 22 }}>
        <Meta style={{ marginBottom: 14 }}>Pielęgnacja</Meta>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
        }}>
          {plant.sun_requirement && (
            <CareBox Icon={Icons.Sun} iconColor="var(--t-amber)"
              lab="Słońce" val={plant.sun_requirement} />
          )}
          {plant.water_needs && (
            <CareBox Icon={Icons.Drop} iconColor="var(--t-sage)"
              lab="Podlewanie" val={plant.water_needs} />
          )}
          {plant.soil_type && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-rust)"
              lab="Gleba" val={plant.soil_type} sub={plant.soil_ph ? `pH ${plant.soil_ph}` : ''} />
          )}
          {plant.npk_needs && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-sage)"
              lab="NPK" val={plant.npk_needs} sub={plant.npk_ratio_recommended || ''} />
          )}
        </div>
      </div>

      {plant.fertilization_frequency && (
        <div style={{ padding: '0 24px', marginBottom: 22 }}>
          <Meta style={{ marginBottom: 8 }}>Nawożenie</Meta>
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
            fontSize: 14, color: 'var(--t-text)',
          }}>{plant.fertilization_frequency}</div>
        </div>
      )}

      {/* Z tej samej rodziny (P4) */}
      <FamilySection plantId={plant.id} mobile />

      {/* Actions */}
      <div style={{ padding: '0 24px 30px' }}>
        <Button
          full
          onClick={() => onAddToGarden?.(plant)}
          icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 3v10M3 8h10" stroke="var(--t-bg)" strokeWidth="1.6" strokeLinecap="round" />
          </svg>}
        >Dodaj do ogrodu</Button>
        <Link
          to={`/problemy?plant=${encodeURIComponent(plant.display_name || plant.name)}`}
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <div style={{
            marginTop: 10, padding: '12px 16px', borderRadius: 10,
            background: 'transparent',
            border: '1px solid var(--t-line-2)',
            color: 'var(--t-text-dim)',
            fontFamily: 'var(--t-mono)', fontSize: 12,
            letterSpacing: '.14em', textTransform: 'uppercase',
            textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 200ms ease, color 200ms ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--t-rust)';
            e.currentTarget.style.color = 'var(--t-rust)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--t-line-2)';
            e.currentTarget.style.color = 'var(--t-text-dim)';
          }}
          >🐛 Możliwe problemy →</div>
        </Link>
      </div>

      {/* Powrót do katalogu / mojego ogrodu */}
      <BackToCatalogLinks mobile />
    </div>
  );
}

// ── BackToCatalogLinks — sekcja powrotu do listy ─────────────────
function BackToCatalogLinks({ mobile }) {
  return (
    <div style={{
      padding: mobile ? '8px 24px 30px' : '24px 0 0',
      display: 'flex', flexDirection: mobile ? 'column' : 'row',
      gap: 10, alignItems: mobile ? 'stretch' : 'center',
      borderTop: mobile ? '1px solid var(--t-line)' : 'none',
      marginTop: mobile ? 18 : 0,
    }}>
      <Link to="/plants?scope=all&restore=1" style={{ textDecoration: 'none', flex: mobile ? 'none' : 1 }}>
        <div style={{
          padding: '12px 16px', borderRadius: 10,
          border: '1px solid var(--t-line-2)',
          background: 'var(--t-bg-elev)',
          color: 'var(--t-text)',
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.16em', textTransform: 'uppercase',
          textAlign: 'center', cursor: 'pointer',
        }}>← Cały katalog roślin</div>
      </Link>
      <Link to="/plants?scope=my" style={{ textDecoration: 'none', flex: mobile ? 'none' : 1 }}>
        <div style={{
          padding: '12px 16px', borderRadius: 10,
          border: '1px solid var(--t-line-2)',
          background: 'transparent',
          color: 'var(--t-text-dim)',
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.16em', textTransform: 'uppercase',
          textAlign: 'center', cursor: 'pointer',
        }}>♥ Mój ogród</div>
      </Link>
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ plant, onAddToGarden }) {
  return (
    <div className="t-stagger" style={{
      padding: '40px 48px 60px',
      display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 48,
    }}>
      <div>
        <PlantHero plant={plant} huge />
        {plant.notes && (
          <div style={{ marginTop: 24 }}>
            <PlantQuote notes={plant.notes} />
          </div>
        )}
      </div>

      <div>
        {/* F3 — Twoje oceny tej rośliny (sprawdzone u mnie) */}
        <VarietyReviewHint plantName={plant.display_name || plant.name} />

        {/* Stats */}
        {(plant.days_to_harvest > 0 || plant.spacing || plant.hardiness_zone) && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1, background: 'var(--t-line-2)',
            borderRadius: 12, overflow: 'hidden', marginBottom: 32,
          }}>
            {plant.days_to_harvest > 0 && <Stat lab="Do zbioru" val={plant.days_to_harvest} unit="dni" />}
            {plant.spacing         && <Stat lab="Rozstaw" val={plant.spacing} />}
            {plant.hardiness_zone  && <Stat lab="Strefa" val={plant.hardiness_zone} />}
          </div>
        )}

        {/* Care */}
        <Meta style={{
          marginBottom: 16, paddingBottom: 8,
          borderBottom: '1px solid var(--t-line-2)',
        }}>Pielęgnacja</Meta>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10, marginBottom: 32,
        }}>
          {plant.sun_requirement && (
            <CareBox Icon={Icons.Sun} iconColor="var(--t-amber)"
              lab="Słońce" val={plant.sun_requirement} />
          )}
          {plant.water_needs && (
            <CareBox Icon={Icons.Drop} iconColor="var(--t-sage)"
              lab="Podlewanie" val={plant.water_needs} />
          )}
          {plant.soil_type && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-rust)"
              lab="Gleba" val={plant.soil_type} sub={plant.soil_ph ? `pH ${plant.soil_ph}` : ''} />
          )}
          {plant.npk_needs && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-sage)"
              lab="NPK" val={plant.npk_needs} sub={plant.npk_ratio_recommended || ''} />
          )}
          {plant.fertilization_frequency && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-sage)"
              lab="Nawożenie" val={plant.fertilization_frequency} />
          )}
          {plant.spacing && (
            <CareBox Icon={Icons.Sprout} iconColor="var(--t-sage)"
              lab="Rozstaw / głębokość" val={plant.spacing} sub={plant.planting_depth || ''} />
          )}
        </div>

        {/* Companion plants */}
        {(plant.companion_plants || plant.avoid_plants) && (
          <>
            <Meta style={{
              marginBottom: 16, paddingBottom: 8,
              borderBottom: '1px solid var(--t-line-2)',
            }}>Sąsiedztwo</Meta>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {plant.companion_plants && (
                <div>
                  <Meta color="sage" style={{ marginBottom: 4 }}>Lubi sąsiedztwo</Meta>
                  <div style={{ fontSize: 14, color: 'var(--t-text)' }}>{plant.companion_plants}</div>
                </div>
              )}
              {plant.avoid_plants && (
                <div>
                  <Meta color="rust" style={{ marginBottom: 4 }}>Unika</Meta>
                  <div style={{ fontSize: 14, color: 'var(--t-text)' }}>{plant.avoid_plants}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Z tej samej rodziny (P4) */}
        <FamilySection plantId={plant.id} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            onClick={() => onAddToGarden?.(plant)}
            icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 3v10M3 8h10" stroke="var(--t-bg)" strokeWidth="1.6" strokeLinecap="round" />
            </svg>}
          >Dodaj do ogrodu</Button>
          <Link
            to={`/problemy?plant=${encodeURIComponent(plant.display_name || plant.name)}`}
            style={{ textDecoration: 'none' }}
          >
            <Button variant="ghost">🐛 Diagnoza problemów</Button>
          </Link>
        </div>

        <BackToCatalogLinks />
      </div>
    </div>
  );
}

// ── BedAsPlantDetail — render bedu jako "rośliny" gdy ID = bed-N ──
// Pokazuje nazwę, odmianę, zdjęcie, notatkę, lokalizację (działka + rząd),
// daty (sadzenia, oczekiwany zbiór), próbuje znaleźć match w katalogu po nazwie
// żeby dorzucić dodatkowe info (kategoria, łacińska, wymagania).
function BedAsPlantDetail({ bedId, navigate, isPhone }) {
  const { data: plotsData, loading } = useApi('/api/plots/all-with-details');

  // Znajdź bed po ID we wszystkich plotach
  const bed = React.useMemo(() => {
    if (!plotsData) return null;
    for (const p of plotsData) {
      const found = (p.beds || []).find(b => b.id === bedId);
      if (found) return { ...found, plot_name: p.name, plot_id: p.id };
    }
    return null;
  }, [plotsData, bedId]);

  // Spróbuj znaleźć pasującą roślinę w katalogu po plant_name
  const { data: catalogMatch } = useApi(
    bed?.plant_name
      ? `/api/plants?limit=2000`
      : null,
    { deps: [bed?.plant_name] }
  );

  const matchedPlant = React.useMemo(() => {
    if (!bed?.plant_name || !catalogMatch?.data) return null;
    const target = bed.plant_name.toLowerCase().trim();
    return catalogMatch.data.find(p => {
      const dn = (p.display_name || '').toLowerCase();
      const n = (p.name || '').toLowerCase().replace(/_/g, ' ');
      return dn === target || n === target ||
             dn.startsWith(target + ' ') || target.startsWith(dn + ' ');
    });
  }, [bed?.plant_name, catalogMatch]);

  if (loading) return <Layout active="plants"><LoadingState label="Ładuję dane grządki…" /></Layout>;
  if (!bed) {
    return (
      <Layout active="plants">
        <EmptyState
          title="Ta grządka już nie istnieje"
          description="Mogła zostać usunięta albo należy do innego ogrodu. Wróć do swojego planu — kolekcja czeka."
          action={<Link to="/plots" style={{ textDecoration: 'none' }}>
            <Button>← Wróć do działek</Button>
          </Link>}
        />
      </Layout>
    );
  }

  const imgRaw = bed.image_path || bed.display_image;
  const img = imgRaw && (imgRaw.startsWith('http') || imgRaw.startsWith('/') ? imgRaw : '/' + imgRaw);

  const planted = bed.planted_date ? new Date(bed.planted_date) : null;
  const expectedHarvest = bed.expected_harvest_date ? new Date(bed.expected_harvest_date) : null;
  const today = new Date();
  const daysSincePlant = planted ? Math.floor((today - planted) / 86400000) : null;
  const daysToHarvest = expectedHarvest ? Math.floor((expectedHarvest - today) / 86400000) : null;

  return (
    <Layout active="plants" topBar={<TopBar
      left={<Link to={`/plots/${bed.plot_id}`} style={{
        fontFamily: 'var(--t-mono)', fontSize: 11,
        letterSpacing: '.14em', textTransform: 'uppercase',
        color: 'var(--t-text-dim)', textDecoration: 'none',
      }}>← {bed.plot_name}</Link>}
    />}>
      <div style={{
        maxWidth: 720, margin: '0 auto',
        padding: 'clamp(20px, 4vw, 32px)',
      }}>
        {/* Hero z zdjęciem albo gradientem */}
        <div style={{
          height: isPhone ? 220 : 320,
          borderRadius: 14,
          background: img
            ? `linear-gradient(180deg, transparent 50%, rgba(15,20,16,0.7) 100%), url(${img}) center / cover`
            : 'linear-gradient(135deg, var(--t-sage-deep), var(--t-sage))',
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', bottom: 16, left: 18, right: 18,
            color: '#fff',
          }}>
            <Meta color="sage" style={{ marginBottom: 6, color: 'rgba(168,196,158,0.9)' }}>
              Z Twojej działki · rząd {bed.row_number}
            </Meta>
            <Display size={isPhone ? 28 : 36} style={{
              display: 'block', letterSpacing: '-.02em',
              color: '#fff', textShadow: '0 2px 6px rgba(0,0,0,0.6)',
            }}>{bed.plant_name || 'Roślina'}</Display>
            {bed.plant_variety && (
              <div style={{
                fontSize: 14, fontStyle: 'italic',
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                marginTop: 4,
              }}>{bed.plant_variety}</div>
            )}
          </div>
        </div>

        {/* Stat strip — daty i wiek */}
        {(planted || expectedHarvest) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 10, marginBottom: 22,
          }}>
            {planted && (
              <BedStatCard
                label="Posadzono"
                value={planted.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
                sub={daysSincePlant != null
                  ? (daysSincePlant > 0 ? `${daysSincePlant} dni temu`
                     : daysSincePlant === 0 ? 'dziś'
                     : `za ${Math.abs(daysSincePlant)} dni`)
                  : null}
                tone="sage"
              />
            )}
            {expectedHarvest && (
              <BedStatCard
                label="Spodziewany zbiór"
                value={expectedHarvest.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })}
                sub={daysToHarvest != null
                  ? (daysToHarvest > 0 ? `za ${daysToHarvest} dni` : daysToHarvest === 0 ? 'dziś!' : `${Math.abs(daysToHarvest)} dni temu`)
                  : null}
                tone={daysToHarvest != null && daysToHarvest <= 7 ? 'amber' : 'dim'}
              />
            )}
            {bed.actual_harvest_date && (
              <BedStatCard
                label="Zbierano"
                value={new Date(bed.actual_harvest_date).toLocaleDateString('pl-PL')}
                sub={bed.yield_amount ? `${bed.yield_amount} ${bed.yield_unit || ''}` : null}
                tone="amber"
              />
            )}
          </div>
        )}

        {/* Notatka usera */}
        {bed.note && (
          <div style={{
            padding: 16, borderRadius: 12,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            borderLeft: '3px solid var(--t-amber)',
            marginBottom: 22,
          }}>
            <Meta color="amber" style={{ marginBottom: 8 }}>📝 Notatka</Meta>
            <div style={{
              fontSize: 14, color: 'var(--t-text)',
              lineHeight: 1.6, fontStyle: 'italic',
            }}>„{bed.note}"</div>
          </div>
        )}

        {/* Lokalizacja — link do działki */}
        <div style={{
          padding: 16, borderRadius: 12,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          marginBottom: 22,
        }}>
          <Meta style={{ marginBottom: 10 }}>📍 Gdzie rośnie</Meta>
          <Link to={`/plots/${bed.plot_id}`} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', borderRadius: 8,
            background: 'var(--t-bg)',
            border: '1px solid var(--t-line)',
            textDecoration: 'none',
            color: 'var(--t-text)',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{bed.plot_name}</div>
              <div style={{
                fontSize: 11, color: 'var(--t-text-mute)', marginTop: 2,
                fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
              }}>RZĄD {bed.row_number}</div>
            </div>
            <span style={{ color: 'var(--t-sage)', fontSize: 18 }}>→</span>
          </Link>
        </div>

        {/* Match z katalogu — jeśli znaleziono */}
        {matchedPlant && (
          <div style={{
            padding: 16, borderRadius: 12,
            background: 'rgba(168, 196, 158, 0.08)',
            border: '1px solid rgba(168, 196, 158, 0.30)',
            marginBottom: 22,
          }}>
            <Meta color="sage" style={{ marginBottom: 10 }}>
              📚 Znaleziono w katalogu
            </Meta>
            <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.6, marginBottom: 12 }}>
              Twoja roślina pasuje do <strong style={{ color: 'var(--t-text)' }}>
                {matchedPlant.display_name}
              </strong>
              {matchedPlant.latin_name && (
                <> (<em>{matchedPlant.latin_name}</em>)</>
              )}.
              W bazie mamy więcej informacji o tej roślinie.
            </div>
            <Link to={`/plants/${matchedPlant.id}`} style={{ textDecoration: 'none' }}>
              <Button variant="primary">Zobacz pełen opis →</Button>
            </Link>
          </div>
        )}

        {!matchedPlant && (
          <div style={{
            padding: 16, borderRadius: 12,
            background: 'rgba(220, 180, 100, 0.06)',
            border: '1px solid rgba(220, 180, 100, 0.20)',
            marginBottom: 22,
            fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55,
          }}>
            ℹ️ Ta roślina nie ma odpowiednika w naszym katalogu 1100+ pozycji.
            Możesz dodać jej szczegółowy opis żeby przyszłe sadzenie było łatwiejsze.{' '}
            <Link to="/plants" style={{ color: 'var(--t-sage)' }}>
              Przejdź do katalogu →
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}

function BedStatCard({ label, value, sub, tone }) {
  const c = tone === 'sage' ? 'var(--t-sage)'
          : tone === 'amber' ? 'var(--t-amber)'
          : 'var(--t-text-dim)';
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
    }}>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-text-mute)', marginBottom: 6,
      }}>{label}</div>
      <div style={{
        fontSize: 14, color: 'var(--t-text)', fontWeight: 500,
        letterSpacing: '-.01em',
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 11, color: c, marginTop: 4, fontFamily: 'var(--t-mono)', letterSpacing: '.08em' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Plant Detail screen root ─────────────────────────────────────
export default function PlantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPhone } = useViewport();

  // ID może być:
  //   - katalogowe (np. '526', '1450') — fetch /api/plants/:id
  //   - bedowe (np. 'bed-20') — ręczny wpis z grządki, render z danych bed
  const isBedId = id && id.startsWith('bed-');
  const bedId = isBedId ? parseInt(id.slice(4), 10) : null;
  const plantId = isBedId ? null : id;

  const { data: plant, loading, error } = useApi(
    plantId ? `/api/plants/${plantId}` : null,
    { deps: [plantId] }
  );

  if (isBedId) {
    return <BedAsPlantDetail bedId={bedId} navigate={navigate} isPhone={isPhone} />;
  }

  if (loading) return <Layout active="plants"><LoadingState /></Layout>;
  if (error)   return <Layout active="plants"><ErrorState error={error} onRetry={() => window.location.reload()} /></Layout>;
  if (!plant)  return <Layout active="plants"><EmptyState title="Roślina nie znaleziona" /></Layout>;

  // Handler "Dodaj do ogrodu" — przekazuje plant do Plot.jsx przez URL query params
  // Plot.jsx wykryje add_bed=true i otworzy BedFormModal z prefilled rośliną
  const handleAddToGarden = (p) => {
    const params = new URLSearchParams({
      add_bed: '1',
      plant_id: String(p.id),
      plant_name: p.display_name || p.name || '',
    });
    if (p.latin_name) params.set('plant_variety', '');
    if (p.days_to_harvest) params.set('days_to_harvest', String(p.days_to_harvest));
    // W1: zapamiętaj origin (ta strona rośliny), żeby Plot.jsx wrócił tu po ANULOWANIU
    // modalu — wcześniej user zostawał w /plots bez drogi powrotu do bazy roślin.
    params.set('from', window.location.pathname + window.location.search);
    navigate(`/plots?${params.toString()}`);
  };

  if (isPhone) {
    return (
      <Layout active="plants">
        <MobileContent plant={plant} onAddToGarden={handleAddToGarden} />
      </Layout>
    );
  }

  return (
    <Layout
      active="plants"
      topBar={<TopBar
        left={
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            color: 'var(--t-text-dim)', fontSize: 13,
          }}>
            <Link to="/plants?restore=1" style={{
              color: 'var(--t-text-dim)', textDecoration: 'none',
            }}>Rośliny</Link>
            <span style={{ opacity: 0.5 }}>/</span>
            <span style={{ color: 'var(--t-sage)' }}>{categoryLabel(plant.category)}</span>
            <span style={{ opacity: 0.5 }}>/</span>
            <span style={{ color: 'var(--t-text)' }}>{plant.display_name || plant.name}</span>
          </div>
        }
        right={
          <Button onClick={() => handleAddToGarden(plant)}>+ Do ogrodu</Button>
        }
      />}
    >
      <DesktopContent plant={plant} onAddToGarden={handleAddToGarden} />
    </Layout>
  );
}
